import { useState } from "react";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { approveContentPart, rejectContentPart } from "../../api/adminContentApi";

const MySwal = withReactContent(Swal);

interface ChapterModerationRowProps {
  partId: string;
  partTitle: string;
  contentTitle: string;
  authorName: string;
  createdAt: string;
  initialIsFreePreview: boolean;
  initialIsPremium: boolean;
  onChanged?: () => void;
}

export default function ChapterModerationRow(props: ChapterModerationRowProps) {
  const {
    partId,
    partTitle,
    contentTitle,
    authorName,
    createdAt,
    initialIsFreePreview,
    initialIsPremium,
    onChanged,
  } = props;

  const [isFreePreview, setIsFreePreview] = useState<boolean>(initialIsFreePreview);
  const [isPremium, setIsPremium] = useState<boolean>(initialIsPremium);
  const [loading, setLoading] = useState(false);

  const handleApprove = async () => {
    const confirm = await MySwal.fire({
      title: "Approve this chapter?",
      text: `${partTitle} (${contentTitle})`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Approve",
      cancelButtonText: "Cancel",
    });
    if (!confirm.isConfirmed) {
      return;
    }

    setLoading(true);
    try {
      const success = await approveContentPart({ partId, isFreePreview, isPremium });
      if (success) {
        await MySwal.fire("Success", "Content part approved.", "success");
        onChanged?.();
      } else {
        await MySwal.fire("Error", "Approval failed.", "error");
      }
    } catch (err) {
      console.error("approve error", err);
      await MySwal.fire("Error", "Approval failed.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    const { value: reason, isConfirmed } = await MySwal.fire({
      title: "Reject chapter",
      input: "text",
      inputPlaceholder: "Optional reason",
      showCancelButton: true,
      confirmButtonText: "Reject",
      cancelButtonText: "Cancel",
    });
    if (!isConfirmed) {
      return;
    }

    setLoading(true);
    try {
      const success = await rejectContentPart({ partId, reason: reason ?? undefined });
      if (success) {
        await MySwal.fire("Success", "Content part rejected.", "success");
        onChanged?.();
      } else {
        await MySwal.fire("Error", "Rejection failed.", "error");
      }
    } catch (err) {
      console.error("reject error", err);
      await MySwal.fire("Error", "Rejection failed.", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-between gap-4 border-b border-gray-200 py-3">
      <div className="flex flex-col">
        <div className="text-sm font-semibold">{partTitle}</div>
        <div className="text-xs text-gray-500">{contentTitle}</div>
        <div className="text-xs text-gray-400">
          By {authorName} - {new Date(createdAt).toLocaleString()}
        </div>
        <div className="mt-1 text-[11px] text-gray-500">
          Free preview chapters do not consume quota; premium chapters require quota or Z tokens.
        </div>
        <div className="flex items-center gap-6">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="h-4 w-4"
              checked={isFreePreview}
              onChange={(e) => setIsFreePreview(e.target.checked)}
              disabled={loading}
            />
            Free preview?
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="h-4 w-4"
              checked={isPremium}
              onChange={(e) => setIsPremium(e.target.checked)}
              disabled={loading}
            />
            Premium?
          </label>
          <div className="flex items-center gap-2">
            <button
              onClick={handleApprove}
              disabled={loading}
              className="rounded bg-green-600 px-3 py-1 text-sm text-white hover:bg-green-700 disabled:opacity-50"
            >
              Approve
            </button>
            <button
              onClick={handleReject}
              disabled={loading}
              className="rounded bg-red-600 px-3 py-1 text-sm text-white hover:bg-red-700 disabled:opacity-50"
            >
              Reject
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
